// Movement families for the 3D demo: every exercise belongs to one, and each family has one motion clip on the shared
// figure (apps/web/public/demos/clips/<clip>.json). An exercise without its own demo shows its family's motion,
// labeled "Similar movement", with its own equipment in the hands; its own clip (built-in, free or recorded) always wins.
// Combinations chain families in one clip (goblet squat to press, thruster, clean and press, burpee, renegade row).
import type { DemoHold, DemoProps, Exercise } from '../types';

export interface DemoFamilyInfo {
  /** Motion clip id (file name in public/demos/clips/). */
  clip: string;
  /** Plain name, for the docs and tests. */
  name: string;
  /** How the hands hold equipment in this motion; null: the hands are busy (floor, bar) or the clip carries its own bell. */
  hold: DemoHold | null;
  /** The session adds a side view (hinge, squat and lunge depth). */
  sideView: boolean;
  /** A weight held in the middle rests straight down (on the hips or the chest) instead of following the arms. */
  hangDown?: boolean;
  /** Clip for one-arm exercises (the free arm does something natural instead of copying the working one). */
  oneArmClip?: string;
}

const f = (clip: string, name: string, hold: DemoHold | null, sideView = false, hangDown = false, oneArmClip?: string): DemoFamilyInfo =>
  ({ clip, name, hold, sideView, ...(hangDown ? { hangDown } : {}), ...(oneArmClip ? { oneArmClip } : {}) });

export const DEMO_FAMILIES = {
  squat: f('squat', 'Squat', 'C', true),
  goblet_squat: f('goblet_squat', 'Goblet or front squat', 'C', true),
  back_squat: f('back_squat', 'Back squat', 'C', true),
  single_leg_squat: f('single_leg_squat', 'Single-leg squat', null),
  lunge: f('lunge', 'Lunge and split squat', 'LR', true),
  lunge_curl: f('lunge_curl', 'Reverse lunge with curl', 'LR', true),
  lateral_lunge: f('lateral_lunge', 'Lateral lunge', 'C'),
  hinge: f('hinge', 'Hip hinge (deadlift)', 'C', true),
  kettlebell_deadlift: f('kettlebell_deadlift', 'Kettlebell deadlift', null, true),
  single_leg_rdl: f('single_leg_rdl', 'Single-leg deadlift', 'R', true),
  swing: f('kettlebell_swing', 'Kettlebell swing', null, true),
  clean: f('clean', 'Clean', 'LR', true, false, 'clean_one'),
  snatch: f('snatch', 'Snatch', 'LR', true, false, 'snatch_one'),
  overhead_press: f('overhead_press', 'Overhead press', 'LR', false, false, 'overhead_press_one'),
  thruster: f('thruster', 'Thruster (squat and press)', 'LR', true),
  goblet_squat_press: f('goblet_squat_press', 'Goblet squat to press', 'C', true),
  clean_and_press: f('clean_and_press', 'Clean and press', 'LR', true, false, 'clean_and_press_one'),
  floor_press: f('floor_press', 'Bench or floor press (lying)', 'LR'),
  push_up: f('push_up', 'Push-up', null),
  row: f('row', 'Bent-over row', 'LR', true, false, 'row_one'),
  renegade_row: f('renegade_row', 'Renegade row (plank and row)', 'LR'),
  pull_up: f('pull_up', 'Pull-up (bar drawn with the figure)', null),
  hanging_leg_raise: f('hanging_leg_raise', 'Hanging leg raise (bar drawn with the figure)', null),
  dip: f('dip', 'Dip', null),
  curl: f('curl', 'Curl', 'LR'),
  triceps_extension: f('triceps_extension', 'Overhead triceps extension', 'C'),
  lateral_raise: f('lateral_raise', 'Lateral raise', 'LR'),
  glute_bridge: f('glute_bridge', 'Glute bridge and hip thrust', 'C', false, true),
  calf_raise: f('calf_raise', 'Calf raise', 'LR'),
  carry: f('carry', 'Carry', 'LR'),
  crunch: f('crunch', 'Crunch and sit-up', 'C', false, true),
  leg_raise: f('leg_raise', 'Lying leg raise', null),
  dead_bug: f('dead_bug', 'Dead bug', null),
  bird_dog: f('bird_dog', 'Bird dog', null),
  plank: f('plank', 'Plank', null),
  side_plank: f('side_plank', 'Side plank', null),
  superman: f('superman', 'Superman', null),
  mountain_climber: f('mountain_climber', 'Mountain climber', null),
  bear_crawl: f('bear_crawl', 'Crawl', null),
  jumping_jack: f('jumping_jack', 'Jumping jack', null),
  jog: f('jog', 'Jog and high knees', null),
  burpee: f('burpee', 'Burpee', null),
  jump: f('jump', 'Squat jump', null, true),
  hop: f('hop', 'Small hops (jump rope)', null),
  russian_twist: f('russian_twist', 'Russian twist', 'C'),
  woodchop: f('woodchop', 'Standing rotation', 'C'),
  get_up: f('get_up', 'Get-up', 'R'),
  side_bend: f('side_bend', 'Side bend and windmill', 'R'),
  forward_fold: f('forward_fold', 'Standing forward fold', null),
  lunge_stretch: f('lunge_stretch', 'Kneeling lunge stretch', null),
  pigeon: f('pigeon', 'Pigeon and hip stretches', null),
  seated_stretch: f('seated_stretch', 'Seated stretch', null),
  childs_pose: f('childs_pose', "Child's pose", null),
  cobra: f('cobra', 'Cobra', null),
  cat_cow: f('cat_cow', 'Cat-cow', null),
  downward_dog: f('downward_dog', 'Downward dog', null),
  supine_stretch: f('supine_stretch', 'Lying stretch', null),
  arm_stretch: f('arm_stretch', 'Standing arm and neck stretch', null),
  quad_stretch: f('quad_stretch', 'Standing quad stretch', null),
  arm_circles: f('arm_circles', 'Arm circles', null),
  leg_swing: f('leg_swing', 'Leg swings', null),
  breathing: f('breathing', 'Calm breathing', null),
  windmill: f('windmill', 'Kettlebell windmill (hip hinge, bell overhead)', 'R'),
  pallof_press: f('pallof_press', 'Pallof press (anti-rotation press-out)', 'C'),
  hip_circles: f('hip_circles', 'Hip circles', null),
  lying_leg_curl: f('lying_leg_curl', 'Lying leg curl', null),
  seated_leg_curl: f('seated_leg_curl', 'Seated leg curl', null),
  leg_extension: f('leg_extension', 'Leg extension', null),
  clamshell: f('clamshell', 'Clamshell', null),
  side_leg_raise: f('side_leg_raise', 'Side-lying leg raise (hip abduction)', null),
  bike: f('bike', 'Cycling', null),
  elliptical: f('elliptical', 'Elliptical stride', null),
  shadow_boxing: f('shadow_boxing', 'Shadow boxing', null),
  pike_push_up: f('pike_push_up', 'Pike push-up', null),
  handstand_push_up: f('handstand_push_up', 'Handstand push-up at the wall', null),
  pullover: f('pullover', 'Pullover', 'C'),
  upright_row: f('upright_row', 'Upright row and high pull', 'C'),
  nordic_curl: f('nordic_curl', 'Nordic hamstring curl', null),
  rowing_machine: f('rowing_machine', 'Rowing machine stroke', 'C'),
  l_sit: f('l_sit', 'L-sit hold', null),
  calf_stretch: f('calf_stretch', 'Wall calf stretch', null),
  barbell_clean: f('barbell_clean', 'Barbell power clean', 'C', true),
  barbell_snatch: f('barbell_snatch', 'Barbell power snatch', 'C', true),
  shrug: f('shrug', 'Shrug', 'LR'),
  run: f('run', 'Running', null),
  walk: f('walk', 'Walking', null),
  shuffle: f('shuffle', 'Lateral shuffle', null),
  single_leg_stand: f('single_leg_stand', 'Single-leg balance', null),
  skip: f('skip', 'A-skip', null),
} satisfies Record<string, DemoFamilyInfo>;

export type DemoFamily = keyof typeof DEMO_FAMILIES;

// Built-in exercises by family. Each id appears exactly once (tested). `[id, props]` sets how the family is shown.
type Entry = string | [string, DemoProps];
const ONE: DemoProps = { hold: 'R', oneArm: true };
const NONE: DemoProps = { equipment: 'none' };
/** Kettlebells on the floor would sit below the hands of the plank motion: dumbbells stand in for them. */
const DUMBBELLS: DemoProps = { equipment: 'dumbbell' };

const BY_FAMILY: Record<DemoFamily, Entry[]> = {
  squat: ['squat', ['bosu_squat', NONE], 'sumo_squat', 'prisoner_squat', 'pause_squat', 'wall_sit', 'sissy_squat', 'deep_squat_hold', ['band_squat', NONE]],
  goblet_squat: ['goblet_squat', 'kettlebell_front_squat', ['double_front_squat', { hold: 'LR' }], 'overhead_squat_kb', 'front_squat', 'dumbbell_goblet_squat', ['dumbbell_squat', { hold: 'LR' }], 'swing_to_goblet_squat'],
  back_squat: ['back_squat', 'box_squat', ['smith_squat', { equipment: 'barbell' }], ['hack_squat', NONE], ['leg_press', NONE]],
  single_leg_squat: ['single_leg_squat', 'shrimp_squat', 'box_pistol'],
  lunge: ['reverse_lunge', 'forward_lunge', 'walking_lunge', 'curtsy_lunge', 'split_squat', 'bulgarian_split_squat', 'step_up', 'lunge_jump',
    ['goblet_reverse_lunge', { hold: 'R' }], ['rack_lunge', { hold: 'R' }], ['lunge_pass_through', { hold: 'R' }], ['kettlebell_step_up', { hold: 'R' }],
    ['barbell_lunge', NONE], 'dumbbell_lunge', 'dumbbell_walking_lunge', 'dumbbell_bulgarian_split_squat', 'dumbbell_step_up'],
  lunge_curl: [['reverse_lunge_curl', ONE]],
  lateral_lunge: ['lateral_lunge', 'cossack_squat', 'kettlebell_lateral_lunge', 'kettlebell_cossack_squat', 'cossack_shift', 'skaters', ['band_lateral_walk', NONE], 'lateral_bound'],
  hinge: ['deadlift', 'sumo_deadlift', 'romanian_deadlift', 'trap_bar_deadlift', 'good_morning', ['dumbbell_rdl', { hold: 'LR' }], 'good_morning_bodyweight',
    ['back_extension', NONE], 'cable_pull_through', ['band_good_morning', NONE], ['ski_erg', NONE]],
  kettlebell_deadlift: ['kettlebell_deadlift', 'kettlebell_rdl', 'suitcase_deadlift', 'kettlebell_good_morning', 'kettlebell_sumo_squat'],
  single_leg_rdl: ['single_leg_rdl', 'single_leg_kb_deadlift', 'dumbbell_single_leg_rdl', 'hip_airplane'],
  swing: ['kettlebell_swing', 'one_arm_swing', 'hand_to_hand_swing', 'american_swing', 'kettlebell_swing_deadstop'],
  clean: [['kettlebell_clean', ONE], ['alternating_clean', ONE], 'double_clean', ['kettlebell_dead_clean', ONE]],
  snatch: [['kettlebell_snatch', ONE], ['half_snatch', ONE], ['dumbbell_snatch', ONE]],
  overhead_press: [['kettlebell_press', ONE], 'double_press', ['bottoms_up_press', ONE], ['half_kneeling_press', ONE], 'overhead_press', 'push_press',
    ['kettlebell_push_press', ONE], ['kettlebell_jerk', ONE], 'dumbbell_shoulder_press', 'arnold_press', 'shoulder_press_machine', ['band_overhead_press', NONE],
    ['landmine_press', NONE], ['overhead_hold', ONE], ['overhead_carry', ONE], 'wall_slides'],
  thruster: ['kettlebell_thruster', 'barbell_thruster', 'dumbbell_thruster', ['clean_squat_press', ONE]],
  goblet_squat_press: ['goblet_squat_press', 'wall_ball', 'med_ball_slam'],
  clean_and_press: [['clean_and_press', ONE], 'dumbbell_clean_and_press'],
  floor_press: [['kettlebell_floor_press', ONE], 'bench_press', 'incline_bench_press', 'close_grip_bench_press', 'dumbbell_bench_press', 'incline_dumbbell_press',
    'dumbbell_floor_press', 'dumbbell_fly', ['chest_press_machine', NONE], 'smith_bench_press', ['band_chest_press', NONE], ['pec_deck', NONE], ['cable_fly', NONE],
  ],
  push_up: ['push_up', 'knee_push_up', 'wall_push_up', 'incline_push_up', 'decline_push_up', 'diamond_push_up', 'wide_push_up', 'archer_push_up', 'hindu_push_up',
    'spiderman_push_up', 'clap_push_up', 'plank_to_push_up', 'scapular_push_up'],
  row: [['kettlebell_row', ONE], 'bent_over_double_row', 'gorilla_row', 'barbell_row', 'pendlay_row', 't_bar_row', ['landmine_row', { equipment: 'barbell' }], ['dumbbell_row', ONE],
    'chest_supported_row', 'dumbbell_bent_over_row', 'seated_row_machine', 'seated_cable_row', ['face_pull', NONE], ['band_row', NONE], ['band_face_pull', NONE],
    'rear_delt_fly', ['reverse_pec_deck', NONE], 'table_row', 'inverted_row'],
  renegade_row: [['renegade_row', DUMBBELLS], 'dumbbell_renegade_row', ['kettlebell_pass_plank_drag', NONE], ['kettlebell_man_maker', DUMBBELLS], 'shoulder_tap', 'plank_with_reach'],
  pull_up: ['pull_up', 'chin_up', 'neutral_grip_pull_up', 'negative_pull_up', 'band_assisted_pull_up', 'scapular_pull_up', 'dead_hang', 'flexed_arm_hang', 'muscle_up',
    'lat_pulldown', 'close_grip_pulldown', 'straight_arm_pulldown', ['assisted_pull_up_machine', { equipment: 'bar' }], ['band_lat_pulldown', { equipment: 'bar' }]],
  hanging_leg_raise: ['hanging_knee_raise', 'hanging_leg_raise', 'toes_to_bar'],
  dip: ['dip', 'negative_dip', 'support_hold', 'bench_dip', 'assisted_dip_machine'],
  curl: [['kettlebell_curl', ONE], 'barbell_curl', 'preacher_curl', 'dumbbell_curl', 'hammer_curl', ['concentration_curl', ONE], 'incline_dumbbell_curl', 'cable_curl',
    ['band_curl', NONE], 'machine_curl', 'wrist_curl'],
  triceps_extension: ['overhead_triceps_extension', 'kettlebell_triceps_extension', ['cable_overhead_triceps_extension', NONE], ['band_triceps_extension', NONE],
    'triceps_kickback', ['triceps_pushdown', NONE], ['machine_triceps_extension', NONE], 'skull_crusher'],
  lateral_raise: ['lateral_raise', 'front_raise', ['cable_lateral_raise', NONE], ['band_pull_apart', NONE]],
  glute_bridge: ['glute_bridge', 'single_leg_glute_bridge', 'hip_thrust_bodyweight', 'frog_pump', 'glute_bridge_march', 'kettlebell_hip_thrust', 'hip_thrust',
    ['band_glute_bridge', NONE]],
  calf_raise: ['calf_raise', 'single_leg_calf_raise', 'dumbbell_calf_raise', ['standing_calf_raise_machine', NONE], 'ankle_rocks'],
  carry: ['farmers_carry_kb', ['suitcase_carry', { hold: 'R' }], 'rack_carry', 'dumbbell_farmers_carry'],
  crunch: ['crunch', 'sit_up', 'butterfly_sit_up', 'bicycle_crunch', 'toe_touch_crunch', 'heel_taps', 'kettlebell_sit_up', ['cable_crunch', NONE], ['ab_crunch_machine', NONE],
    ['stability_ball_crunch', NONE], 'v_up', 'tuck_up'],
  leg_raise: ['lying_leg_lowers', 'flutter_kicks', 'scissor_kicks', 'reverse_crunch', 'hollow_hold', 'windshield_wipers'],
  dead_bug: ['dead_bug', 'dead_bug_hold'],
  bird_dog: ['bird_dog', 'donkey_kick', 'fire_hydrant', ['glute_kickback_machine', NONE], ['cable_glute_kickback', NONE]],
  plank: ['plank', 'high_plank', 'plank_hip_twist', ['stir_the_pot', NONE], ['ab_wheel_rollout', NONE]],
  side_plank: ['side_plank', 'side_plank_hip_dip', 'copenhagen_plank'],
  superman: ['superman', 'arch_hold', 'prone_y_raise', 'reverse_snow_angel'],
  mountain_climber: ['mountain_climber', 'mountain_climber_slow', 'cross_body_mountain_climber', 'plank_jacks'],
  bear_crawl: ['bear_crawl', 'crab_walk', 'bear_plank_hold'],
  jumping_jack: ['jumping_jack', 'seal_jack', 'half_jack', 'star_jump'],
  jog: ['jog_in_place', 'high_knees', 'marching_high_knees', 'butt_kicks', 'fast_feet', 'box_step_up_fast', 'stair_climber', 'jump_rope_high_knees', 'ladder_in_in_out_out'],
  burpee: ['burpee', 'burpee_push_up', 'half_burpee', 'sprawl', 'step_back_burpee', 'burpee_tuck_jump', 'burpee_box_jump_over', ['devil_press', NONE],
    ['kettlebell_burpee_deadlift', NONE]],
  jump: ['squat_jump', 'tuck_jump', 'broad_jump', 'frog_jump', 'box_jump', 'depth_drop'],
  hop: ['line_hops', 'line_hops_forward', 'pogo_hops', 'single_leg_hop', 'jump_rope_basic', 'jump_rope_alternate_foot', 'double_under'],
  russian_twist: ['russian_twist', 'kettlebell_russian_twist', 'med_ball_russian_twist', 'boat_hold'],
  woodchop: ['dumbbell_woodchop', ['cable_woodchop', NONE], 'med_ball_rotational_throw', 'torso_twists', 'kettlebell_halo',
    'kettlebell_around_the_world', 'kettlebell_figure_eight'],
  get_up: ['turkish_get_up', 'half_get_up', 'get_up_sit_down'],
  side_bend: ['side_bend_stretch', 'lat_stretch_wall'],
  forward_fold: ['standing_hamstring_stretch', 'standing_forward_fold', 'jefferson_curl_bodyweight'],
  lunge_stretch: ['hip_flexor_hamstring_stretch', 'kneeling_hip_flexor_stretch', 'lizard_stretch', 'worlds_greatest_stretch', 'couch_stretch'],
  pigeon: ['pigeon_stretch', 'ninety_ninety_switch'],
  seated_stretch: ['seated_forward_fold', 'butterfly_stretch', 'seated_straddle'],
  childs_pose: ['childs_pose', 'puppy_pose', 'frog_stretch'],
  cobra: ['cobra_stretch', 'sphinx_pose', 'spinal_waves'],
  cat_cow: ['cat_cow', 'thread_the_needle'],
  downward_dog: ['downward_dog', 'inchworm', 'stability_ball_pike'],
  supine_stretch: ['supine_twist', 'knees_to_chest', 'happy_baby', 'figure_four_stretch', 'legs_up_the_wall', 'pretzel_stretch', 'thoracic_open_book'],
  arm_stretch: ['cross_body_shoulder_stretch', 'overhead_triceps_stretch', 'doorway_chest_stretch', 'neck_side_stretch', 'wrist_flexor_stretch', 'neck_rolls',
    'wrist_circles', 'shoulder_rolls'],
  quad_stretch: ['standing_quad_stretch'],
  arm_circles: ['arm_circles', 'band_dislocates'],
  leg_swing: ['leg_swings_front', 'leg_swings_side'],
  breathing: ['deep_breathing', 'box_breathing', 'belly_breathing'],
  windmill: ['kettlebell_windmill'],
  pallof_press: [['pallof_press', NONE]],
  hip_circles: ['hip_circles'],
  lying_leg_curl: [['lying_leg_curl', NONE]],
  seated_leg_curl: [['seated_leg_curl', NONE]],
  leg_extension: [['leg_extension', NONE]],
  clamshell: ['clamshell'],
  side_leg_raise: ['side_lying_leg_raise', ['hip_abduction_machine', NONE]],
  bike: ['stationary_bike', 'assault_bike', ['road_ride', NONE], ['gravel_ride', NONE], ['indoor_ride', NONE], ['mtb_ride', NONE]],
  elliptical: ['elliptical'],
  shadow_boxing: ['shadow_boxing'],
  pike_push_up: ['pike_push_up'],
  handstand_push_up: ['handstand_push_up'],
  pullover: ['dumbbell_pullover', 'kettlebell_pullover'],
  upright_row: ['kettlebell_upright_row', 'kettlebell_high_pull', 'sumo_high_pull'],
  nordic_curl: ['nordic_curl'],
  rowing_machine: ['rowing_machine', ['water_row', NONE]],
  l_sit: ['l_sit'],
  calf_stretch: ['standing_calf_stretch'],
  barbell_clean: ['power_clean', 'hang_power_clean'],
  barbell_snatch: ['power_snatch'],
  shrug: ['barbell_shrug', 'dumbbell_shrug'],
  run: ['run', 'treadmill_run', 'trail_run', 'track_run', 'strides', 'shuttle_run', 'cone_zigzag'],
  walk: ['walk', 'incline_walk', 'hike', 'nordic_walk'],
  shuffle: ['lateral_shuffle', 'ladder_lateral'],
  single_leg_stand: ['single_leg_balance', 'single_leg_balance_eyes_closed', 'bosu_single_leg_balance'],
  skip: ['a_skip'],
};

/**
 * Exercises we cannot show well with any family motion yet: no figure ("No 3D demo yet" and the record button) rather
 * than a wrong one.
 */
export const NO_DEMO_FAMILY = [
  'med_ball_chest_pass', 'stability_ball_hamstring_curl', 'hip_adduction_machine',
  // Endurance and coordination: nothing we can draw convincingly yet (water, skis, skates, reactions, juggling).
  'pool_swim', 'open_water_swim', 'xc_ski_classic', 'xc_ski_skate', 'ski_touring', 'inline_skating',
  'ladder_icky_shuffle', 't_drill', 'carioca', 'b_skip', 'balance_board_stand',
  'ball_drop_reaction', 'partner_call_reaction', 'wall_ball_toss', 'two_ball_juggle',
] as const;

/** The family and display settings of every built-in exercise, by exercise id; family null: no figure on purpose. */
export const DEMO_FAMILY_OF: Record<string, { family: DemoFamily | null; props?: DemoProps }> = {};
for (const [family, entries] of Object.entries(BY_FAMILY) as [DemoFamily, Entry[]][]) {
  for (const entry of entries) {
    const [id, props] = typeof entry === 'string' ? [entry, undefined] : entry;
    if (Object.hasOwn(DEMO_FAMILY_OF, id)) throw new Error(`${id} is in two demo families`);
    DEMO_FAMILY_OF[id] = props ? { family, props } : { family };
  }
}
for (const id of NO_DEMO_FAMILY) {
  if (Object.hasOwn(DEMO_FAMILY_OF, id)) throw new Error(`${id} is in a demo family and in NO_DEMO_FAMILY`);
  DEMO_FAMILY_OF[id] = { family: null };
}

/** A known family id (own keys only: "toString" or "constructor" are not families). */
export function isDemoFamily(v: unknown): v is DemoFamily {
  return typeof v === 'string' && Object.hasOwn(DEMO_FAMILIES, v);
}

const PROP_KINDS = new Set(['kettlebell', 'dumbbell', 'barbell', 'bar', 'ball', 'none']);
const HOLDS = new Set(['C', 'LR', 'L', 'R']);

/** The first problem of stored demo display settings (shared library data), or null. */
export function demoPropsProblem(v: unknown): string | null {
  if (v === undefined) return null;
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return 'demoProps is not an object';
  const o = v as Record<string, unknown>;
  for (const k of Object.keys(o)) if (!['equipment', 'hold', 'oneArm', 'tempo'].includes(k)) return `demoProps has an unknown field ${k.slice(0, 20)}`;
  if (o.equipment !== undefined && !PROP_KINDS.has(o.equipment as string)) return 'demoProps.equipment is not a known kind';
  if (o.hold !== undefined && !HOLDS.has(o.hold as string)) return 'demoProps.hold is not C, LR, L or R';
  if (o.oneArm !== undefined && typeof o.oneArm !== 'boolean') return 'demoProps.oneArm is not true or false';
  if (o.tempo !== undefined && !(typeof o.tempo === 'number' && Number.isFinite(o.tempo) && o.tempo >= 0.5 && o.tempo <= 2)) return 'demoProps.tempo is not between 0.5 and 2';
  return null;
}

/** A family from the pattern and category, for exercises people add (no family set). */
export function guessDemoFamily(e: Pick<Exercise, 'pattern' | 'category' | 'equipment'>): DemoFamily {
  if (e.category === 'stretch') return 'forward_fold';
  if (e.category === 'mobility') return 'arm_circles';
  if (e.category === 'breathing') return 'breathing';
  switch (e.pattern) {
    case 'squat': return e.equipment === 'kettlebell' || e.equipment === 'dumbbell' ? 'goblet_squat' : e.equipment === 'barbell' ? 'back_squat' : 'squat';
    case 'hinge': return e.equipment === 'kettlebell' ? 'kettlebell_deadlift' : 'hinge';
    case 'lunge': return 'lunge';
    case 'horizontal_push': return e.equipment === 'bodyweight' || e.equipment === 'none' ? 'push_up' : 'floor_press';
    case 'vertical_push': return e.equipment === 'dip_bars' ? 'dip' : 'overhead_press';
    case 'horizontal_pull': return 'row';
    case 'vertical_pull': return 'pull_up';
    case 'carry': return 'carry';
    case 'core': return 'plank';
    case 'rotation': return 'russian_twist';
    case 'locomotion': return 'jog';
    case 'jump': return 'jump';
    case 'full_body': return 'burpee';
    default: break;
  }
  if (e.category === 'cardio') return 'jog';
  if (e.category === 'core') return 'crunch';
  if (e.category === 'plyometric') return 'jump';
  return e.equipment === 'bodyweight' || e.equipment === 'none' ? 'squat' : 'curl';
}

type FamilyInput = Pick<Exercise, 'id' | 'demoFamily' | 'copiedFrom' | 'pattern' | 'category' | 'equipment'>;

/** The built-in mapping that applies: the exercise's own id, else the built-in it was copied from. */
function mapped(e: Pick<Exercise, 'id' | 'copiedFrom'>) {
  if (Object.hasOwn(DEMO_FAMILY_OF, e.id)) return DEMO_FAMILY_OF[e.id];
  if (e.copiedFrom && Object.hasOwn(DEMO_FAMILY_OF, e.copiedFrom)) return DEMO_FAMILY_OF[e.copiedFrom];
  return undefined;
}

/**
 * The family of any exercise, or null for none: the built-in mapping (by id, or of the exercise it was copied from, so
 * copies follow fixes to the mapping), else its own valid setting, else a guess from pattern and category.
 */
export function demoFamilyOf(e: FamilyInput): DemoFamily | null {
  const m = mapped(e);
  if (m) return m.family;
  if (isDemoFamily(e.demoFamily)) return e.demoFamily;
  return guessDemoFamily(e);
}

/** The display settings for an exercise's family motion: the built-in mapping's, else its own valid ones. */
export function demoPropsOf(e: Pick<Exercise, 'id' | 'demoProps' | 'copiedFrom'>): DemoProps {
  const m = mapped(e);
  if (m) return m.props ?? {};
  return demoPropsProblem(e.demoProps) === null && e.demoProps ? e.demoProps : {};
}
